/* [PATCH izdaja-predloge v1] Nastavitve → Izdani računi → Izgled računa: galerija 10 predlog (sličice prek Gotenberga),
   izbira, predogled PDF vzorca, nalaganje logotipa / žiga / podpisa (PNG, JPG, WEBP, SVG do 2 MB).
   API: /izdaja/{cid}/predloge (+ /{koda}/slicica, /{koda}/vzorec), /izdaja/{cid}/slika/{logo|zig|podpis}. Slike so za prijavljene
   (Authorization), zato se berejo prek api kot blob -> object URL. */
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, Eye, Loader2, Trash2, Upload } from 'lucide-react';
import { api } from '../../lib/api';
import { usePotrdi } from '../izdaja/PotrdiDialog'; // [PATCH izdaja-potrdi-dialog v1]

type Predloga = { koda: string; ime: string; opis: string; logo: string };
type Vrsta = 'logo' | 'zig' | 'podpis';
const VRSTE: { id: Vrsta; napis: string; namig: string }[] = [
  { id: 'logo', napis: 'Logotip', namig: 'v glavi računa (mesto določa oblika; brez logotipa se izpiše naziv)' },  // [PATCH izdaja-oblike v1]
  { id: 'zig', napis: 'Žig', namig: 'desno spodaj, čez podpisno črto' },
  { id: 'podpis', napis: 'Podpis', namig: 'desno spodaj, nad podpisno črto' },
];
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50';
const btnS = btn + ' bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-blue-400';

function napaka(e: any, privzeto: string) {
  const d = e?.response?.data?.detail;
  toast.error(typeof d === 'string' ? d : (d?.sporocilo || privzeto));
}

async function blobUrl(pot: string): Promise<string | null> {
  try { const r = await api.get(pot, { responseType: 'blob' }); return URL.createObjectURL(r.data); } catch { return null; }
}

export function IzdajaIzgled({ companyId, stranka, del }: { companyId: string; stranka: boolean; del?: 'predloga' | 'slike' }) { // [PATCH izdaja-carovnik v1] del = samo en blok (čarovnik)
  const [potrdi, potrdiEl] = usePotrdi(); // [PATCH izdaja-potrdi-dialog v1]
  const [predloge, setPredloge] = useState<Predloga[]>([]);
  const [izbrana, setIzbrana] = useState<string>('01');
  const [slike, setSlike] = useState<Record<string, boolean>>({});
  const [slikeUrl, setSlikeUrl] = useState<Record<string, string | null>>({});
  const [slicice, setSlicice] = useState<Record<string, string | null>>({});
  const [delam, setDelam] = useState<string>('');
  const vhodi = useRef<Record<string, HTMLInputElement | null>>({});
  const verzija = useRef(0);

  const nalozi = async () => {
    try {
      const r = await api.get(`/izdaja/${companyId}/predloge`);
      setPredloge(r.data?.predloge || []); setIzbrana(r.data?.izbrana || '01'); setSlike(r.data?.slike || {});
      const v = ++verzija.current;
      const urls: Record<string, string | null> = {};
      for (const vr of VRSTE) urls[vr.id] = r.data?.slike?.[vr.id] ? await blobUrl(`/izdaja/${companyId}/slika/${vr.id}`) : null;
      if (v === verzija.current) setSlikeUrl(urls);
      setSlicice({});
      for (const p of (r.data?.predloge || []) as Predloga[]) {
        const u = await blobUrl(`/izdaja/${companyId}/predloge/${p.koda}/slicica`);
        if (v === verzija.current) setSlicice(s => ({ ...s, [p.koda]: u }));
      }
    } catch (e) { napaka(e, 'Predlog ni bilo mogoče naložiti.'); }
  };
  useEffect(() => { if (companyId) nalozi(); /* eslint-disable-line */ }, [companyId]);

  const izberi = async (koda: string) => {
    if (koda === izbrana) return;
    setDelam('izbira');
    try { await api.put(`/izdaja/${companyId}/predloge`, { koda }); setIzbrana(koda); toast.success('Predloga izbrana.'); }
    catch (e) { napaka(e, 'Izbira ni uspela.'); } finally { setDelam(''); }
  };

  const predogled = async (koda: string) => {
    setDelam('predogled' + koda);
    try {
      const r = await api.get(`/izdaja/${companyId}/predloge/${koda}/vzorec`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { napaka(e, 'Predogleda ni bilo mogoče izrisati.'); } finally { setDelam(''); }
  };

  const nalozi_sliko = async (vrsta: Vrsta, f: File | undefined) => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Slika je prevelika (največ 2 MB).'); return; }
    const fd = new FormData(); fd.append('datoteka', f);
    setDelam('slika' + vrsta);
    try {
      await api.post(`/izdaja/${companyId}/slika/${vrsta}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${VRSTE.find(v => v.id === vrsta)?.napis} naložen.`); await nalozi();
    } catch (e) { napaka(e, 'Nalaganje ni uspelo.'); } finally { setDelam(''); if (vhodi.current[vrsta]) vhodi.current[vrsta]!.value = ''; }
  };

  const odstrani_sliko = async (vrsta: Vrsta) => {
    if (!(await potrdi({ naslov: `Odstranim ${VRSTE.find(v => v.id === vrsta)?.napis?.toLowerCase()}?`, besedilo: 'Slika se odstrani z novih dokumentov; že izdani PDF-i ostanejo.', potrdiNapis: 'Odstrani', nevarno: true }))) return; // [PATCH izdaja-potrdi-dialog v1]
    setDelam('slika' + vrsta);
    try { await api.delete(`/izdaja/${companyId}/slika/${vrsta}`); toast.success('Odstranjeno.'); await nalozi(); }
    catch (e) { napaka(e, 'Odstranitev ni uspela.'); } finally { setDelam(''); }
  };

  return (
    <div className="space-y-3">
      {!del && <div className="font-semibold pt-2">Izgled računa (predloga, logotip, žig, podpis)</div>}
      {del !== 'predloga' && <div className="text-xs text-gray-500">Slike so neobvezne. Če jih ni, ostane na računu prazna podpisna črta s podpisnikom. Formati PNG, JPG, WEBP, SVG do 2 MB; za žig in podpis priporočamo PNG s prozornim ozadjem.</div>}

      {del !== 'predloga' && <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {VRSTE.map(v => (
          <div key={v.id} className="rounded-xl border dark:border-gray-700 p-3 bg-white dark:bg-gray-900">
            <div className="text-sm font-medium">{v.napis} <span className="text-xs text-gray-500 font-normal">— {v.namig}</span></div>
            <div className="mt-2 h-24 flex items-center justify-center rounded bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] dark:bg-none dark:bg-gray-800 overflow-hidden">
              {slikeUrl[v.id] ? <img src={slikeUrl[v.id] as string} alt={v.napis} className="max-h-20 max-w-full object-contain" /> : <span className="text-xs text-gray-400">{slike[v.id] ? 'nalagam …' : 'ni naloženo'}</span>}
            </div>
            <div className="mt-2 flex gap-2">
              <input ref={el => { vhodi.current[v.id] = el; }} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={e => nalozi_sliko(v.id, e.target.files?.[0])} />
              <button className={btnS} disabled={!!delam} onClick={() => vhodi.current[v.id]?.click()}>{delam === 'slika' + v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {slike[v.id] ? 'Zamenjaj' : 'Naloži'}</button>
              {slike[v.id] && <button className={btnS} disabled={!!delam} onClick={() => odstrani_sliko(v.id)} title="Odstrani"><Trash2 className="w-4 h-4 text-rose-600" /></button>}
            </div>
          </div>
        ))}
      </div>}

      {del !== 'slike' && <div className="text-sm font-medium pt-1">Predloga <span className="text-xs text-gray-500 font-normal">— velja za vse nove izdane dokumente; že izdani PDF-i ostanejo, kot so</span></div>}
      {del !== 'slike' && <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {predloge.map(p => (
          <div key={p.koda} className={'rounded-xl border-2 p-2 bg-white dark:bg-gray-900 cursor-pointer transition ' + (izbrana === p.koda ? 'border-blue-600' : 'border-gray-200 dark:border-gray-700 hover:border-blue-300')} onClick={() => izberi(p.koda)}>
            <div className="aspect-[210/297] w-full rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              {slicice[p.koda] ? <img src={slicice[p.koda] as string} alt={p.ime} className="w-full h-full object-cover object-top" /> : <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
            </div>
            <div className="mt-2 flex items-center justify-between gap-1">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1">{izbrana === p.koda && <Check className="w-4 h-4 text-blue-600" />}{p.ime}</div>
                <div className="text-[11px] text-gray-500 truncate" title={p.opis}>{p.logo}</div>{/* [PATCH izdaja-oblike v1] primerna za */}
              </div>
              <button className={btnS + ' px-2'} title="Predogled PDF" disabled={!!delam} onClick={e => { e.stopPropagation(); predogled(p.koda); }}>{delam === 'predogled' + p.koda ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>
        ))}
      </div>}
      {stranka && <div className="text-xs text-gray-500">Kot stranka lahko izbiraš predlogo in nalagaš slike svojega podjetja.</div>}
      {potrdiEl}{/* [PATCH izdaja-potrdi-dialog v1] */}
    </div>
  );
}
